import React, { useState, useEffect, forwardRef, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Table,
  Toast,
  ToastContainer,
  Dropdown,
  Spinner,
} from "react-bootstrap";
import { FaArrowLeft, FaCalendar, FaSearch, FaTimes } from "react-icons/fa";
import { Sidebar } from "../common/Sidebar";
import { Navbar } from "../common/Navbar";
import CommonDropdown from "../common/common_dropDown";
import {
  getTransactionsAPI,
  createPaymentAPI,
  addAttachmentAPI,
} from "./payment-api";
import { useLocation, useNavigate } from "react-router-dom";
import "../common/Util.css";
import fileUploadIcon from "../../assets/svg/file_upload.svg";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import numberFormat from "../common/number-format";
import { ReactComponent as DownArrowIc } from "../../assets/svg/down_arrow_ics.svg";
import ReactPaginate from "react-paginate";
import { ReactComponent as RarIc } from "../../assets/svg/rar_ic.svg";
import { ReactComponent as LarIc } from "../../assets/svg/lar_ic.svg";
import ProfileImageModal from "../common/common-preview";

// Custom DatePicker Input
const CustomDateInput = forwardRef(({ value, onClick, placeholder }, ref) => (
  <div style={{ position: "relative" }}>
    <Form.Control
      type="text"
      value={value}
      onClick={onClick}
      placeholder={placeholder}
      ref={ref}
      style={{
        border: "1px solid #E5E7EB",
        borderRadius: "8px",
        padding: "10px 36px 10px 12px",
        fontSize: "14px",
        color: value ? "#1A1A1A" : "#9CA3AF",
        cursor: "pointer",
      }}
      readOnly
    />
    <FaCalendar
      style={{
        position: "absolute",
        right: "12px",
        top: "50%",
        transform: "translateY(-50%)",
        fontSize: "16px",
        color: "#6366F1",
        pointerEvents: "none",
      }}
    />
  </div>
));

const PaymentCreate = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const location = useLocation();

  const [vendorOption, setVendorOption] = useState(null);
  const cleanFloat = (val) => {
    if (val === undefined || val === null) return 0;
    const cleanVal = val.toString().replace(/,/g, '');
    return parseFloat(cleanVal) || 0;
  };
  const [pageCount, setPageCount] = useState(0);
  const [itemOffset, setItemOffset] = useState(0);
  useEffect(() => {
    if (location.state?.vendor) {
      setVendorOption(location.state.vendor);
    }
  }, [location.state]);
  const handlePageClick = useCallback((event) => {
    setItemOffset(event.selected);
  }, []);
  const [showProfilePopup, setShowProfilePopup] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState("");
  const openFile = (fileUrl = "", fileName = "") => {
    // prefer filename extension (CREATE flow)
    const source = (fileName || fileUrl).toLowerCase();
    const cleanSource = source.split("?")[0];

    // 🖼 Images → popup
    if (
      cleanSource.endsWith(".png") ||
      cleanSource.endsWith(".jpg") ||
      cleanSource.endsWith(".jpeg") ||
      cleanSource.endsWith(".gif") ||
      cleanSource.endsWith(".webp")
    ) {
      if (!fileUrl) return; // no preview without URL
      setSelectedProfile(fileUrl);
      setShowProfilePopup(true);
      return;
    }

    // 📄 PDF → open directly
    if (cleanSource.endsWith(".pdf")) {
      if (!fileUrl) return;
      setSelectedProfile(fileUrl);
      setShowProfilePopup(true);
      return;
    }

    // 📊 Office files → Office Viewer
    if (
      cleanSource.endsWith(".xls") ||
      cleanSource.endsWith(".xlsx") ||
      cleanSource.endsWith(".doc") ||
      cleanSource.endsWith(".docx") ||
      cleanSource.endsWith(".ppt") ||
      cleanSource.endsWith(".pptx")
    ) {
      if (!fileUrl) return;

      const officeViewerUrl =
        "https://view.officeapps.live.com/op/view.aspx?src=" +
        encodeURIComponent(fileUrl);

      window.open(officeViewerUrl, "_blank", "noopener,noreferrer");
      return;
    }

    // ⬇ Fallback
    if (fileUrl) {
      window.open(fileUrl, "_blank", "noopener,noreferrer");
    }
  };

  const [paymentAmount, setPaymentAmount] = useState(null);
  const [paymentDate, setPaymentDate] = useState(null);
  const [paymentType, setPaymentType] = useState("Select");
  const [paymentTypeOption, setPaymentTypeOption] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [vendorStats, setVendorStats] = useState(null);
  const [allocatedAmounts, setAllocatedAmounts] = useState({});
  const [manuallyEdited, setManuallyEdited] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  // Toast/Snackbar state
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarStatus, setSnackbarStatus] = useState(true); // true = success, false = error

  // Field errors
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchTransactions = async () => {
      if (vendorOption && vendorOption.value) {
        setLoading(true);
        const param = {
          limit: itemsPerPage,
          skip: itemOffset * itemsPerPage,
          search: searchQuery,
        };
        try {
          const response = await getTransactionsAPI(vendorOption.value, param);
          if (response.success) {
            const transactionsObj = response.summary || {};
            const transactionsList = response.transactions || [];
            setTransactions(transactionsList);
            setTotalCount(response.total_rec ?? 0);
            setPageCount(response.total_pgs ?? 0);
            setVendorStats({
              name: vendorOption.label,
              revenue: transactionsObj.total_payment || 0,
              totalInvoices: transactionsObj.total_po || 0,
              totalPayment: transactionsObj.total_payment || 0,
              amountReceived: transactionsObj.total_paid || 0,
              outstanding: transactionsObj.total_balance || 0,
              symbol: transactionsObj.symbol || "&#8377;",
            });
          }
        } catch (error) {
          console.error("Error fetching transactions:", error);
          setTransactions([]);
          setVendorStats(null);
        } finally {
          setLoading(false);
        }
      } else {
        setTransactions([]);
        setVendorStats(null);
        setAllocatedAmounts({});
        setManuallyEdited(new Set());
      }
    };

    fetchTransactions();
  }, [vendorOption, searchQuery, itemOffset, itemsPerPage]);

  useEffect(() => {
    const distributePayment = () => {
      if (
        !paymentAmount ||
        paymentAmount <= 0 ||
        selectedTransactions.length === 0
      ) {
        setAllocatedAmounts((prev) => {
          const updated = {};
          Object.keys(prev).forEach((txnId) => {
            if (
              manuallyEdited.has(parseInt(txnId)) &&
              selectedTransactions.includes(parseInt(txnId))
            ) {
              updated[txnId] = prev[txnId];
            }
          });
          return updated;
        });
        return;
      }

      setAllocatedAmounts((prev) => {
        let remainingPayAmount = cleanFloat(paymentAmount);
        const allocated = { ...prev };

        const manuallyAllocatedAmount = Object.keys(allocated)
          .filter(
            (txnId) =>
              manuallyEdited.has(parseInt(txnId)) &&
              selectedTransactions.includes(parseInt(txnId)),
          )
          .reduce((sum, txnId) => sum + (cleanFloat(allocated[txnId])), 0);

        remainingPayAmount -= manuallyAllocatedAmount;

        const transactionsToAutoAllocate = transactions
          .filter(
            (t) =>
              selectedTransactions.includes(t.id) &&
              !manuallyEdited.has(t.id) &&
              cleanFloat(t.balance_amount) > 0,
          )
          .sort((a, b) => a.id - b.id);

        for (const transaction of transactionsToAutoAllocate) {
          if (remainingPayAmount <= 0) {
            allocated[transaction.id] = 0;
            continue;
          }

          const transactionBalance = cleanFloat(
            transaction.balance_amount,
          );

          if (remainingPayAmount >= transactionBalance) {
            allocated[transaction.id] = transactionBalance;
            remainingPayAmount -= transactionBalance;
          } else {
            allocated[transaction.id] = remainingPayAmount;
            remainingPayAmount = 0;
          }
        }

        Object.keys(allocated).forEach((txnId) => {
          if (!selectedTransactions.includes(parseInt(txnId))) {
            delete allocated[txnId];
          }
        });

        return allocated;
      });
    };

    distributePayment();
  }, [paymentAmount, selectedTransactions, transactions, manuallyEdited]);

  const handleAmountChange = (transactionId, value) => {
    const transaction = transactions.find((t) => t.id === transactionId);
    if (!transaction) return;

    const balance = cleanFloat(transaction.balance_amount);
    let enteredAmount = cleanFloat(value);

    if (enteredAmount > balance) {
      enteredAmount = balance;
    }
    if (enteredAmount < 0) {
      enteredAmount = 0;
    }

    setManuallyEdited((prev) => {
      const updated = new Set(prev);
      updated.add(transactionId);
      return updated;
    });

    setAllocatedAmounts((prev) => ({
      ...prev,
      [transactionId]: enteredAmount,
    }));
  };

  const handleFileRemove = (fileId) => {
    setUploadedFiles((prev) => prev.filter((file) => file.id !== fileId));
  };

  const handleSelectFile = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*,.pdf,.doc,.docx";
    input.multiple = true;
    input.onchange = (e) => {
      const files = Array.from(e.target.files);
      const newFiles = files.map((file, index) => {
        const sizeKB = (file.size / 1024).toFixed(2);
        const isImage = file.type.startsWith("image/");
        let preview = null;

        if (isImage) {
          preview = URL.createObjectURL(file);
        }

        return {
          id: Date.now() + index,
          name: file.name,
          size: `${sizeKB} KB`,
          type: file.type,
          preview: preview,
          file: file, // Store actual file object for upload
        };
      });
      setUploadedFiles((prev) => [...prev, ...newFiles]);
    };
    input.click();
  };

  const toggleTransactionSelection = (id) => {
    setSelectedTransactions((prev) => {
      if (prev.includes(id)) {
        return prev.filter((item) => item !== id);
      }

      // Check if the new transaction has the same currency as already selected ones
      if (prev.length > 0) {
        const firstTxn = transactions.find((t) => t.id === prev[0]);
        const currentTxn = transactions.find((t) => t.id === id);

        if (firstTxn && currentTxn && firstTxn.symbol !== currentTxn.symbol) {
          setSnackbarMessage("Cannot select transactions with different currencies");
          setSnackbarStatus(false);
          setShowSnackbar(true);
          return prev;
        }
      }

      return [...prev, id];
    });
  };

  const toggleSelectAll = () => {
    if (selectedTransactions.length === transactions.length) {
      setSelectedTransactions([]);
    } else {
      if (transactions.length === 0) return;

      const firstSymbol = transactions[0].symbol;
      const allSame = transactions.every((t) => t.symbol === firstSymbol);

      if (!allSame) {
        setSnackbarMessage(
          "Transactions have different currencies. Select manually.",
        );
        setSnackbarStatus(false);
        setShowSnackbar(true);
        return;
      }

      setSelectedTransactions(transactions.map((t) => t.id));
    }
  };

  const handleVendorSelect = (item) => {
    setVendorOption(item);
  };

  const getDisplaySymbol = () => {
    if (selectedTransactions.length > 0) {
      const firstTxn = transactions.find((t) => t.id === selectedTransactions[0]);
      return firstTxn?.symbol || vendorStats?.symbol || "&#8377;";
    }
    return vendorStats?.symbol || "&#8377;";
  };

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });

  const handleSavePayment = async () => {
    if (saving) return; // Prevent multiple clicks
    setSaving(true);

    try {
      // Clear previous errors
      setErrors({});

      // Validation
      const newErrors = {};

      if (!vendorOption) {
        newErrors.customer = "Please select a customer";
      }

      if (!paymentAmount || cleanFloat(paymentAmount) <= 0) {
        newErrors.paymentAmount = "Please enter a valid payment amount";
      }

      if (!paymentDate) {
        newErrors.paymentDate = "Please select a payment date";
      }

      if (!paymentTypeOption || !paymentTypeOption.label) {
        newErrors.paymentType = "Please select a payment type";
      }

      if (selectedTransactions.length === 0) {
        newErrors.transactions = "Please select at least one transaction";
      }

      const totalAllocated = Object.values(allocatedAmounts).reduce(
        (sum, amount) => sum + amount,
        0,
      );
      if (totalAllocated > cleanFloat(paymentAmount)) {
        newErrors.allocation = `Total allocated amount (₹${totalAllocated.toLocaleString(
          "en-IN",
        )}) exceeds payment amount`;
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }

      setSaving(true);

      const formattedDate = paymentDate.toISOString().split("T")[0];
      const transactions = selectedTransactions.map((id) => ({
        id: id,
        amount: Number(allocatedAmounts[id] || 0),
      }));

      const payload = {
        vendor_id: vendorOption.value,
        payment_type_id: paymentTypeOption.value,
        date: formattedDate,
        remarks: remarks || "",
        transactions: transactions,
      };

      const response = await createPaymentAPI(payload);

      if (response.success) {
        const paymentId = response.id;

        if (uploadedFiles.length > 0 && paymentId) {
          try {
            let base64File;
            if (uploadedFiles.length > 0) {
              base64File = await fileToBase64(uploadedFiles[0].file);
            }

            const bodyData = {
              id: paymentId,
              attachment: base64File,
            };

            await addAttachmentAPI(bodyData);
          } catch (attachmentError) {
            console.error("Error uploading attachments:", attachmentError);
          }
        }

        setSnackbarMessage("Payment created successfully!");
        setSnackbarStatus(true);
        setShowSnackbar(true);

        navigate("/payment");
      } else {
        setSnackbarMessage(response.message || "Failed to create payment");
        setSnackbarStatus(false);
        setShowSnackbar(true);
      }
    } catch (error) {
      console.error("Error saving payment:", error);
      setSnackbarMessage(
        error.response?.data?.message || "Failed to save payment",
      );
      setSnackbarStatus(false);
      setShowSnackbar(true);
    } finally {
      setSaving(false);
    }
  };

  const handleSnackbarClose = () => {
    setShowSnackbar(false);
  };

  return (
    <div className="d-flex">
      <ProfileImageModal
        show={showProfilePopup}
        onClose={() => setShowProfilePopup(false)}
        imageUrl={selectedProfile}
      />{" "}
      {/* Sidebar */}
      <Sidebar currentfile="Payment" />
      {/* Main Content */}
      <div className="flex-grow-1">
        <Navbar pageTitle="Payments" />

        <div
          className="pt-2"
          style={{ backgroundColor: "#F8F9FC", minHeight: "100vh" }}
        >
          <Container fluid>
            {/* Header */}
            <Row className="mb-4" style={{ marginBottom: "24px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  <FaArrowLeft
                    style={{
                      fontSize: "18px",
                      color: "#1A1A1A",
                      cursor: "pointer",
                    }}
                    onClick={() => window.history.back()}
                  />
                  <h4
                    style={{
                      margin: 0,
                      fontSize: "20px",
                      fontWeight: "600",
                      color: "#1A1A1A",
                    }}
                  >
                    New payment
                  </h4>
                </div>
                <div style={{ display: "flex", gap: "12px" }}>
                  <Button
                    className="px-4 py-2"
                    style={{
                      backgroundColor: "white",
                      border: "1px solid #E5E7EB",
                      borderRadius: "8px",
                      color: "#374151",
                      fontWeight: "500",
                      fontSize: "14px",
                    }}
                    onClick={() => window.history.back()}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="px-4 py-2 d-flex align-items-center justify-content-center"
                    style={{
                      backgroundColor: "#5B50FE",
                      border: "none",
                      borderRadius: "8px",
                      color: "white",
                      fontWeight: "500",
                      fontSize: "14px",
                      boxShadow: "0 1px 2px rgba(91, 80, 254, 0.2)",
                      opacity: saving ? 0.7 : 1,
                      minWidth: "110px",
                    }}
                    onClick={handleSavePayment}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                      </>
                    ) : (
                      "Save"
                    )}
                  </Button>
                </div>
              </div>
            </Row>
            {/* Main Content */}
            <Row>
              {/* Customer Details - Left Column */}
              <Col md={4}>
                <div
                  style={{
                    backgroundColor: "white",
                    borderRadius: "12px",
                    padding: "24px",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  }}
                >
                  <div
                    className="fs-16 fw-600 mb-3"
                    style={{ color: "#1A1A1A" }}
                  >
                    Vendor details
                  </div>

                  {/* Customer Dropdown */}
                  <div style={{ marginBottom: "8px" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "16px",
                        backgroundColor: "#FAFAFA",
                        borderRadius: "16px",
                        padding: "8px 16px",
                      }}
                    >
                      <div
                        className="fs-13 fw-500 d-flex"
                        style={{ color: "#515159", minWidth: "120px" }}
                      >
                        Vendor<span className="error-text">*</span>
                      </div>
                      <div style={{ flex: 1 }}>
                        <CommonDropdown
                          apiUrl={`${apiUrl}get_vendor?term=`}
                          placeholder="Select"
                          value={vendorOption}
                          onSelect={handleVendorSelect}
                        />
                      </div>
                    </div>
                    {errors.customer && (
                      <div
                        className="fs-12"
                        style={{
                          color: "#EF4444",
                          marginTop: "4px",
                          marginLeft: "8px",
                        }}
                      >
                        {errors.customer}
                      </div>
                    )}
                  </div>

                  {/* Customer Info Card */}
                  {vendorOption && (
                    <div
                      style={{
                        backgroundColor: "#FAFAFA",
                        border: "1px solid #E5E7EB",
                        borderRadius: "8px",
                        padding: "16px",
                        marginBottom: "20px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                        }}
                      >
                        {/* Profile Picture or First Letter */}
                        <div
                          style={{
                            width: "48px",
                            height: "48px",
                            background:
                              vendorOption.image &&
                              vendorOption.image.trim() !== ""
                                ? "transparent"
                                : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                            borderRadius: "8px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            overflow: "hidden",
                          }}
                        >
                          {vendorOption.image &&
                          vendorOption.image.trim() !== "" ? (
                            <img
                              src={vendorOption.image}
                              alt="Profile"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <span
                              style={{
                                fontSize: "20px",
                                fontWeight: "600",
                                color: "white",
                              }}
                            >
                              {(vendorStats?.name || vendorOption.label)
                                ?.charAt(0)
                                .toUpperCase() || "?"}
                            </span>
                          )}
                        </div>

                        <div style={{ flex: 1 }}>
                          <div
                            className="fs-14 fw-600"
                            style={{ color: "#1A1A1A", marginBottom: "4px" }}
                          >
                            {vendorStats?.name || vendorOption.label}
                          </div>
                          <div className="fs-12 fw-500 textPurple">
                            Revenue : <span dangerouslySetInnerHTML={{ __html: vendorStats?.symbol || "&#8377;" }} />{" "}
                            {parseFloat(
                              vendorStats?.revenue || 0,
                            ).toLocaleString("en-IN")}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Stats Grid */}
                  {vendorOption && (
                    <>
                      <Row style={{ marginBottom: "12px" }}>
                        <Col xs={6} style={{ paddingRight: "6px" }}>
                          <div
                            style={{
                              backgroundColor: "#F9FAFB",
                              borderRadius: "8px",
                              padding: "12px",
                              textAlign: "center",
                            }}
                          >
                            <div
                              className="fs-11 fw-500"
                              style={{ color: "#9CA3AF", marginBottom: "8px" }}
                            >
                              Total Transactions
                            </div>
                            <div
                              className="fs-20 fw-700"
                              style={{ color: "#1A1A1A" }}
                            >
                              {vendorStats?.totalInvoices || 0}
                            </div>
                          </div>
                        </Col>
                        <Col xs={6} style={{ paddingLeft: "6px" }}>
                          <div
                            style={{
                              backgroundColor: "#F9FAFB",
                              borderRadius: "8px",
                              padding: "12px",
                              textAlign: "center",
                            }}
                          >
                            <div
                              className="fs-11 fw-500"
                              style={{ color: "#9CA3AF", marginBottom: "8px" }}
                            >
                              Total payment
                            </div>
                            <div
                              className="fs-20 fw-700"
                              style={{ color: "#1A1A1A" }}
                            >
                              <span dangerouslySetInnerHTML={{ __html: vendorStats?.symbol || "&#8377;" }} />{" "}
                              {parseFloat(
                                vendorStats?.totalPayment || 0,
                              ).toLocaleString("en-IN")}
                            </div>
                          </div>
                        </Col>
                      </Row>

                      <Row>
                        <Col xs={6} style={{ paddingRight: "6px" }}>
                          <div
                            style={{
                              backgroundColor: "#F9FAFB",
                              borderRadius: "8px",
                              padding: "12px",
                              textAlign: "center",
                            }}
                          >
                            <div
                              className="fs-11 fw-500"
                              style={{ color: "#9CA3AF", marginBottom: "8px" }}
                            >
                              Amount Received
                            </div>
                            <div
                              className="fs-20 fw-700"
                              style={{ color: "#1A1A1A" }}
                            >
                              <span dangerouslySetInnerHTML={{ __html: vendorStats?.symbol || "&#8377;" }} />{" "}
                              {parseFloat(
                                vendorStats?.amountReceived || 0,
                              ).toLocaleString("en-IN")}
                            </div>
                          </div>
                        </Col>
                        <Col xs={6} style={{ paddingLeft: "6px" }}>
                          <div
                            style={{
                              backgroundColor: "#F9FAFB",
                              borderRadius: "8px",
                              padding: "12px",
                              textAlign: "center",
                            }}
                          >
                            <div
                              className="fs-11 fw-500"
                              style={{ color: "#9CA3AF", marginBottom: "8px" }}
                            >
                              Outstanding
                            </div>
                            <div
                              className="fs-20 fw-700"
                              style={{ color: "#1A1A1A" }}
                            >
                              <span dangerouslySetInnerHTML={{ __html: vendorStats?.symbol || "&#8377;" }} />{" "}
                              {parseFloat(
                                vendorStats?.outstanding || 0,
                              ).toLocaleString("en-IN")}
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </>
                  )}
                </div>
              </Col>

              {/* Payment Details - Right Column */}
              <Col md={8}>
                <div
                  style={{
                    backgroundColor: "white",
                    borderRadius: "12px",
                    padding: "24px",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  }}
                >
                  <h5 className="fs-16 fw-600" style={{ color: "#1A1A1A" }}>
                    Payment details
                  </h5>

                  {/* Payment Amount and Date */}
                  <Row>
                    <Col md={6} style={{ paddingRight: "12px" }}>
                      <div style={{ marginBottom: "16px" }}>
                        <div
                          className="fs-13 fw-500 mb-2 d-flex"
                          style={{ color: "#121417" }}
                        >
                          Pay Amount<span className="error-text">*</span>
                        </div>
                        <div style={{ position: "relative" }}>
                          <span
                            style={{
                              position: "absolute",
                              left: "12px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              fontSize: "14px",
                              color: "#1A1A1A",
                              fontWeight: "600",
                            }}
                          >
                            <span dangerouslySetInnerHTML={{ __html: getDisplaySymbol() }} />
                          </span>
                          <Form.Control
                            type="text"
                            value={paymentAmount}
                            onChange={(e) => setPaymentAmount(e.target.value)}
                            style={{
                              border: "1px solid #E5E7EB",
                              borderRadius: "8px",
                              padding: "10px 12px 10px 28px",
                              fontSize: "14px",
                              color: "#1A1A1A",
                              fontWeight: "600",
                            }}
                          />
                        </div>
                        {errors.paymentAmount && (
                          <div
                            className="fs-12"
                            style={{ color: "#EF4444", marginTop: "4px" }}
                          >
                            {errors.paymentAmount}
                          </div>
                        )}
                      </div>
                    </Col>

                    <Col md={6} style={{ paddingLeft: "12px" }}>
                      <div style={{ marginBottom: "16px" }}>
                        <div
                          className="fs-13 fw-500 mb-2 d-flex"
                          style={{ color: "#121417" }}
                        >
                          Date<span className="error-text">*</span>
                        </div>
                        <DatePicker
                          selected={paymentDate}
                          onChange={(date) => setPaymentDate(date)}
                          dateFormat="dd-MM-yyyy"
                          placeholderText="dd-mm-yyyy"
                          customInput={<CustomDateInput />}
                        />
                        {errors.paymentDate && (
                          <div
                            className="fs-12"
                            style={{ color: "#EF4444", marginTop: "4px" }}
                          >
                            {errors.paymentDate}
                          </div>
                        )}
                      </div>
                    </Col>
                  </Row>

                  {/* Payment Type and Remarks */}
                  <Row>
                    <Col md={6} style={{ paddingRight: "12px" }}>
                      <div style={{ marginBottom: "16px" }}>
                        <div
                          className="fs-13 fw-500 mb-2 d-flex"
                          style={{ color: "#121417" }}
                        >
                          Payment Type<span className="error-text">*</span>
                        </div>
                        <CommonDropdown
                          apiUrl={`${apiUrl}get_payment_type?term=`}
                          placeholder="Select"
                          value={paymentTypeOption}
                          onSelect={(item) => {
                            setPaymentTypeOption(item);
                          }}
                        />
                        {errors.paymentType && (
                          <div
                            className="fs-12"
                            style={{ color: "#EF4444", marginTop: "4px" }}
                          >
                            {errors.paymentType}
                          </div>
                        )}
                      </div>
                    </Col>

                    <Col md={6} style={{ paddingLeft: "12px" }}>
                      <div style={{ marginBottom: "16px" }}>
                        <div
                          className="fs-13 fw-500 mb-2"
                          style={{ color: "#121417" }}
                        >
                          Remarks
                        </div>
                        <Form.Control
                          type="text"
                          placeholder="Enter your comments here..."
                          value={remarks}
                          onChange={(e) => setRemarks(e.target.value)}
                          style={{
                            border: "1px solid #E5E7EB",
                            borderRadius: "8px",
                            padding: "10px 12px",
                            fontSize: "14px",
                            color: "#9CA3AF",
                          }}
                        />
                      </div>
                    </Col>
                  </Row>

                  {/* Payment Type Section */}
                  <div style={{ marginBottom: "20px" }}>
                    <div
                      className="fs-13 fw-500 mb-3"
                      style={{ color: "#121417" }}
                    >
                      Attachments
                    </div>

                    {/* Uploaded Files - Horizontal Scroll */}
                    {uploadedFiles.length > 0 && (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          gap: "12px",
                          marginBottom: "16px",
                          overflowX: "auto",
                          paddingBottom: "8px",
                        }}
                      >
                        {uploadedFiles.map((file) => (
                          <div
                            key={file.id}
                            style={{
                              backgroundColor: "#F9FAFB",
                              borderRadius: "12px",
                              padding: "8px",
                              display: "flex",
                              alignItems: "center",
                              gap: "16px",
                              position: "relative",
                              minWidth: "250px",
                              flexShrink: 0,
                              cursor: "pointer",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              openFile(file.preview, file.name);
                            }}
                          >
                            {/* File Preview/Icon */}
                            {file.preview ? (
                              // Image Preview
                              <div
                                style={{
                                  width: "42px",
                                  height: "42px",
                                  borderRadius: "12px",
                                  overflow: "hidden",
                                  backgroundColor: "#F3F4F6",
                                  flexShrink: 0,
                                }}
                              >
                                <img
                                  src={file.preview}
                                  alt={file.name}
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                  }}
                                />
                              </div>
                            ) : (
                              // Document Icon
                              <div
                                style={{
                                  width: "42px",
                                  height: "42px",
                                  backgroundColor: "#F3F4F6",
                                  borderRadius: "12px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                }}
                              >
                                <svg
                                  width="40"
                                  height="40"
                                  viewBox="0 0 24 24"
                                  fill="#9CA3AF"
                                >
                                  <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11z" />
                                </svg>
                              </div>
                            )}

                            {/* File Info */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div
                                className="fs-14 fw-500"
                                style={{
                                  color: "#1A1A1A",
                                  marginBottom: "6px",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                                title={file.name}
                              >
                                {file.name}
                              </div>
                              <div
                                className="fs-13"
                                style={{
                                  color: "#6B7280",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                }}
                              >
                                <span>{file.size}</span>
                                <span>•</span>
                                <span
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    color: "#10B981",
                                    fontWeight: "500",
                                  }}
                                >
                                  <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 20 20"
                                    fill="#10B981"
                                  >
                                    <path d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" />
                                  </svg>
                                  Uploaded
                                </span>
                              </div>
                            </div>

                            {/* Remove Button */}
                            <FaTimes
                              onClick={(e) => {
                                e.stopPropagation();
                                handleFileRemove(file.id);
                              }}
                              style={{
                                fontSize: "18px",
                                color: "#9CA3AF",
                                cursor: "pointer",
                                flexShrink: 0,
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* File Upload Area */}
                  {uploadedFiles.length === 0 && (
                    <div
                      onClick={handleSelectFile}
                      style={{
                        border: "2px dashed #ADC5FF",
                        borderRadius: "8px",
                        padding: "12px",
                        textAlign: "center",
                        cursor: "pointer",
                        transition: "all 0.2s",
                        backgroundColor: "transparent",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "12px",
                      }}
                    >
                      <div
                        style={{
                          width: "40px",
                          height: "40px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <img
                          src={fileUploadIcon}
                          alt="Upload"
                          width="40"
                          height="40"
                        />
                      </div>
                      <div style={{ textAlign: "left" }}>
                        <div
                          className="fs-13 fw-400"
                          style={{ color: "#374151", marginBottom: "2px" }}
                        >
                          Drag and drop or{" "}
                          <span
                            style={{
                              color: "#5B50FE",
                              cursor: "pointer",
                              fontWeight: "500",
                            }}
                          >
                            Select a file
                          </span>
                        </div>
                        <div className="fs-12" style={{ color: "#9CA3AF" }}>
                          You can add any type of files here.
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </Col>
            </Row>
            <Row>
              {/* Transactions Table */}
              <div
                style={{
                  backgroundColor: "white",
                  borderRadius: "12px",
                  padding: "24px",
                  marginTop: "24px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                }}
              >
                {/* Table Header */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "20px",
                  }}
                >
                  <h6
                    className="fs-12 fw-600"
                    style={{
                      color: "#121417",
                      letterSpacing: "0.5px",
                      margin: 0,
                    }}
                  >
                    TRANSACTIONS ({totalCount})
                  </h6>

                  {/* Search Box */}
                  <div style={{ position: "relative", width: "280px" }}>
                    <FaSearch
                      style={{
                        position: "absolute",
                        left: "12px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        fontSize: "13px",
                        color: "#9CA3AF",
                      }}
                    />
                    <Form.Control
                      type="text"
                      placeholder="Search..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      style={{
                        border: "1px solid #E5E7EB",
                        borderRadius: "8px",
                        padding: "8px 12px 8px 36px",
                        fontSize: "13px",
                        color: "#6B7280",
                        backgroundColor: "#FFFFFF",
                      }}
                    />
                  </div>
                </div>

                {/* Error Messages for Transactions/Allocation */}
                {(errors.transactions || errors.allocation) && (
                  <div style={{ marginBottom: "12px" }}>
                    {errors.transactions && (
                      <div
                        className="fs-12"
                        style={{ color: "#EF4444", marginBottom: "4px" }}
                      >
                        {errors.transactions}
                      </div>
                    )}
                    {errors.allocation && (
                      <div className="fs-12" style={{ color: "#EF4444" }}>
                        {errors.allocation}
                      </div>
                    )}
                  </div>
                )}

                {/* Table */}
                <div
                  style={{
                    overflowX: "auto",
                    overflowY: "auto",
                    maxHeight: "400px",
                  }}
                >
                  <Table style={{ marginBottom: 0 }}>
                    <thead>
                      <tr
                        style={{
                          backgroundColor: "#F9FAFB",
                          borderBottom: "1px solid #E5E7EB",
                        }}
                      >
                        <th
                          style={{
                            padding: "10px 16px",
                            fontSize: "12px",
                            fontWeight: "600",
                            color: "#6B7280",
                            border: "none",
                            width: "50px",
                          }}
                        >
                          <Form.Check
                            type="checkbox"
                            checked={
                              transactions.length > 0 &&
                              selectedTransactions.length ===
                                transactions.length
                            }
                            onChange={toggleSelectAll}
                          />
                        </th>
                        <th
                          className="fs-12 fw-600"
                          style={{
                            padding: "10px 16px",
                            color: "#6B7280",
                            border: "none",
                          }}
                        >
                          Transaction ID
                        </th>
                        <th
                          className="fs-12 fw-600"
                          style={{
                            padding: "10px 16px",
                            color: "#6B7280",
                            border: "none",
                          }}
                        >
                          Purchase Order
                        </th>
                        <th
                          className="fs-12 fw-600"
                          style={{
                            padding: "10px 16px",
                            color: "#6B7280",
                            border: "none",
                          }}
                        >
                          Total amount
                        </th>
                        <th
                          className="fs-12 fw-600"
                          style={{
                            padding: "10px 16px",
                            color: "#6B7280",
                            border: "none",
                          }}
                        >
                          Paid
                        </th>
                        <th
                          className="fs-12 fw-600"
                          style={{
                            padding: "10px 16px",
                            color: "#6B7280",
                            border: "none",
                          }}
                        >
                          Balance
                        </th>
                        <th
                          className="fs-12 fw-600"
                          style={{
                            padding: "10px 16px",
                            color: "#6B7280",
                            border: "none",
                          }}
                        >
                          Amount to Pay
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td
                            colSpan="7"
                            style={{ textAlign: "center", padding: "40px" }}
                          >
                            <div style={{ color: "#666", fontSize: "13px" }}>
                              Loading transactions...
                            </div>
                          </td>
                        </tr>
                      ) : transactions.length === 0 ? (
                        <tr>
                          <td
                            colSpan="7"
                            style={{ textAlign: "center", padding: "40px" }}
                          >
                            <div style={{ color: "#666", fontSize: "13px" }}>
                              {vendorOption
                                ? "No transactions found"
                                : "Please select a vendor to view transactions"}
                            </div>
                          </td>
                        </tr>
                      ) : (
                        transactions.map((transaction, index) => (
                          <tr
                            key={transaction.id}
                            style={{
                              borderBottom:
                                index !== transactions.length - 1
                                  ? "1px solid #F3F4F6"
                                  : "none",
                              backgroundColor: selectedTransactions.includes(
                                transaction.id,
                              )
                                ? "#F9FAFB"
                                : "white",
                            }}
                          >
                            <td
                              style={{
                                padding: "12px 16px",
                                border: "none",
                              }}
                            >
                              <Form.Check
                                type="checkbox"
                                checked={selectedTransactions.includes(
                                  transaction.id,
                                )}
                                onChange={() =>
                                  toggleTransactionSelection(transaction.id)
                                }
                              />
                            </td>
                            <td
                              style={{
                                padding: "12px 16px",
                                fontSize: "13px",
                                color: "#1A1A1A",
                                border: "none",
                                fontWeight: "500",
                              }}
                            >
                              {transaction.transaction_code || "-"}
                            </td>
                            <td
                              style={{
                                padding: "12px 16px",
                                fontSize: "13px",
                                color: "#6B7280",
                                border: "none",
                              }}
                            >
                              {transaction.purchase_order || "-"}
                            </td>
                            <td
                              style={{
                                padding: "12px 16px",
                                fontSize: "13px",
                                color: "#1A1A1A",
                                border: "none",
                                fontWeight: "500",
                              }}
                            >
                              <span dangerouslySetInnerHTML={{ __html: transaction.symbol || vendorStats?.symbol || "&#8377;" }} />{numberFormat(transaction.total_amount)}
                            </td>
                            <td
                              style={{
                                padding: "12px 16px",
                                fontSize: "13px",
                                color: "#10B981",
                                border: "none",
                                fontWeight: "600",
                              }}
                            >
                              <span dangerouslySetInnerHTML={{ __html: transaction.symbol || vendorStats?.symbol || "&#8377;" }} />{numberFormat(transaction.paid_amount)}
                            </td>
                            <td
                              style={{
                                padding: "12px 16px",
                                fontSize: "13px",
                                color: "#EF4444",
                                border: "none",
                                fontWeight: "600",
                              }}
                            >
                              <span dangerouslySetInnerHTML={{ __html: transaction.symbol || vendorStats?.symbol || "&#8377;" }} />{numberFormat(transaction.balance_amount)}
                            </td>
                            <td
                              style={{
                                padding: "12px 16px",
                                border: "none",
                              }}
                            >
                              <div
                                style={{ position: "relative", width: "120px" }}
                              >
                                <span
                                  style={{
                                    position: "absolute",
                                    left: "12px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    fontSize: "13px",
                                    color: "#1A1A1A",
                                    fontWeight: "600",
                                    pointerEvents: "none",
                                  }}
                                >
                                  <span dangerouslySetInnerHTML={{ __html: transaction.symbol || vendorStats?.symbol || "&#8377;" }} />
                                </span>
                                <Form.Control
                                  type="number"
                                  value={allocatedAmounts[transaction.id] || 0}
                                  onChange={(e) =>
                                    handleAmountChange(
                                      transaction.id,
                                      e.target.value,
                                    )
                                  }
                                  disabled={
                                    !selectedTransactions.includes(
                                      transaction.id,
                                    )
                                  }
                                  style={{
                                    border: "1px solid #E5E7EB",
                                    borderRadius: "6px",
                                    padding: "6px 12px 6px 24px",
                                    fontSize: "13px",
                                    color:
                                      allocatedAmounts[transaction.id] > 0
                                        ? "#5B50FE"
                                        : "#9CA3AF",
                                    fontWeight: "600",
                                    backgroundColor:
                                      selectedTransactions.includes(
                                        transaction.id,
                                      )
                                        ? "white"
                                        : "#F9FAFB",
                                  }}
                                  max={transaction.balance_amount}
                                  min={0}
                                />
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>

                {/* Payment Summary */}
                {paymentAmount > 0 &&
                  selectedTransactions.length > 0 &&
                  (() => {
                    const totalAllocated = Object.values(
                      allocatedAmounts,
                    ).reduce((sum, amount) => sum + amount, 0);
                    const remainingBalance =
                      cleanFloat(paymentAmount) - totalAllocated;
                    const isOverAllocated = remainingBalance < 0;

                    return (
                      <>
                        <div
                          style={{
                            marginTop: "16px",
                            padding: "16px",
                            backgroundColor: "#F9FAFB",
                            borderRadius: "8px",
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: "32px",
                          }}
                        >
                          <div>
                            <div
                              className="fs-12 fw-500"
                              style={{ color: "#6B7280", marginBottom: "4px" }}
                            >
                              Total Pay Amount
                            </div>
                            <div
                              className="fs-16 fw-600"
                              style={{ color: "#1A1A1A" }}
                            >
                              <span dangerouslySetInnerHTML={{ __html: getDisplaySymbol() }} />
                              {cleanFloat(paymentAmount).toLocaleString(
                                "en-IN",
                              )}
                            </div>
                          </div>
                          <div>
                            <div
                              className="fs-12 fw-500"
                              style={{ color: "#6B7280", marginBottom: "4px" }}
                            >
                              Total Allocated
                            </div>
                            <div
                              className="fs-16 fw-600"
                              style={{
                                color: isOverAllocated ? "#EF4444" : "#5B50FE",
                              }}
                            >
                              <span dangerouslySetInnerHTML={{ __html: getDisplaySymbol() }} />{totalAllocated.toLocaleString("en-IN")}
                            </div>
                          </div>
                          <div>
                            <div
                              className="fs-12 fw-500"
                              style={{ color: "#6B7280", marginBottom: "4px" }}
                            >
                              Remaining Balance
                            </div>
                            <div
                              className="fs-16 fw-600"
                              style={{
                                color: isOverAllocated ? "#EF4444" : "#10B981",
                              }}
                            >
                              <span dangerouslySetInnerHTML={{ __html: getDisplaySymbol() }} />{remainingBalance.toLocaleString("en-IN")}
                            </div>
                          </div>
                        </div>
                        {isOverAllocated && (
                          <div
                            style={{
                              marginTop: "12px",
                              padding: "12px 16px",
                              backgroundColor: "#FEF2F2",
                              border: "1px solid #FCA5A5",
                              borderRadius: "8px",
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 20 20"
                              fill="#EF4444"
                            >
                              <path
                                fillRule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                clipRule="evenodd"
                              />
                            </svg>
                            <span
                              className="fs-13 fw-500"
                              style={{ color: "#DC2626" }}
                            >
                              Warning: Total allocated amount exceeds the
                              payment amount by <span dangerouslySetInnerHTML={{ __html: getDisplaySymbol() }} />
                              {Math.abs(remainingBalance).toLocaleString(
                                "en-IN",
                              )}
                            </span>
                          </div>
                        )}
                      </>
                    );
                  })()}
              </div>
              <div className="py-3 d-flex justify-content-between align-items-center flex-wrap gap-3">
                <Dropdown>
                  <span className="pagination-color">
                    Records per page: &nbsp;
                  </span>
                  <Dropdown.Toggle
                    id="dropdown-basic"
                    className="bg-transparent pagination-color  border-0 custom-dropdown-toggle p-0"
                  >
                    <span className="bg-white textcolor no-of-rows rounded-3 px-2 py-2">
                      {itemsPerPage} <DownArrowIc />
                    </span>
                  </Dropdown.Toggle>
                  <Dropdown.Menu
                    className="my-2"
                    style={{ minWidth: "unset", width: "30%" }}
                  >
                    {[5, 10, 25, 50, 75, 100].map((size) => (
                      <Dropdown.Item
                        key={size}
                        onClick={() => {
                          setItemsPerPage(size);
                        }}
                        active={itemsPerPage === size}
                        className="text-center"
                      >
                        {size}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>

                <div className="d-flex align-items-center gap-2">
                  <ReactPaginate
                    previousLabel={
                      <div
                        className="rounded-5 bg-white d-flex align-items-center justify-content-center"
                        style={{
                          width: "32px",
                          height: "32px",
                        }}
                      >
                        <LarIc />
                      </div>
                    }
                    nextLabel={
                      <div
                        className="rounded-5 bg-white d-flex align-items-center justify-content-center"
                        style={{
                          width: "32px",
                          height: "32px",
                        }}
                      >
                        <RarIc />
                      </div>
                    }
                    breakLabel={"..."}
                    pageCount={pageCount + 1}
                    marginPagesDisplayed={2}
                    pageRangeDisplayed={3}
                    onPageChange={handlePageClick}
                    containerClassName={"pagination"}
                    activeClassName={"active"}
                  />
                </div>
              </div>
            </Row>
          </Container>
        </div>
      </div>
      {/* Toast/Snackbar */}
      <ToastContainer position="top-end">
        <Toast
          className="d-inline-block m-1"
          bg={snackbarStatus === true ? "success" : "danger"}
          show={showSnackbar}
          onClose={handleSnackbarClose}
          delay={3000}
          autohide
        >
          <Toast.Body style={{ color: "#ffffff" }}>
            {snackbarMessage}
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
};

export default PaymentCreate;
